// src/components/TestimonialsSection/TestimonialsSection.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import axios from 'axios';
import AppContext from '../../../../../context/AppContext';
import '../../../../../assests/css/customer/TestimonialsSection.css';

const DEFAULT_AVATAR = 'https://i.pravatar.cc/150?img=1';

const TestimonialsSection = () => {
  const { baseUrl } = useContext(AppContext);

  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isEnvelopeOpen, setIsEnvelopeOpen] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    let isMounted = true;

    axios.post(`${baseUrl}/customer/feedback/getTestimonials`)
      .then((res) => {
        if (isMounted) setTestimonials(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error('Failed to load testimonials:', err.response?.status, err.response?.data || err.message);
        if (isMounted) setTestimonials([]);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => { isMounted = false; };
  }, [baseUrl]);

  useEffect(() => {
    if (loading || testimonials.length === 0) return;

    const e1 = setTimeout(() => setIsEnvelopeOpen(true), 500);
    const e2 = setTimeout(() => setShowCards(true), 1500);
    return () => {
      clearTimeout(e1);
      clearTimeout(e2);
    };
  }, [loading, testimonials]);

  useEffect(() => {
    if (!showCards || isHovered || testimonials.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, [showCards, isHovered, testimonials.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const handleTouchMove = (e) => { touchEndX.current = e.touches[0].clientX; };
  const handleTouchEnd = () => {
    if (touchStartX.current - touchEndX.current > 50) handleNext();
    if (touchEndX.current - touchStartX.current > 50) handlePrev();
  };

  const getVisibleCards = () => {
    if (testimonials.length === 0) return [];
    const prev = currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1;
    const next = currentIndex === testimonials.length - 1 ? 0 : currentIndex + 1;
    return [testimonials[prev], testimonials[currentIndex], testimonials[next]];
  };

  if (loading || testimonials.length === 0) return null;

  return (
    <div className="customerreviewtestimonials-section">
      <div className="customerreviewtestimonials-header">
        <h3 className="customerreviewtestimonials-title">Words of Appreciation</h3>
        <p className="customerreviewtestimonials-subtitle">
          Hear what our customers say about MOONLIT
        </p>
      </div>

      {!showCards && (
        <div className={`customerreviewenvelope-container ${isEnvelopeOpen ? 'customerreviewopen' : ''}`}>
          <div className="customerreviewenvelope">
            <div className="customerreviewenvelope-flap"></div>
            <div className="customerreviewenvelope-body"></div>
            <div className="customerreviewenvelope-card">
              <img src={testimonials[0].customer?.image || DEFAULT_AVATAR} alt="user" />
            </div>
          </div>
        </div>
      )}

      {showCards && (
        <div
          className="customerreviewtestimonials-carousel"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <button className="customerreviewnav-arrow customerreviewprev-arrow" onClick={handlePrev}>
            <ChevronLeft />
          </button>

          <div className="customerreviewcards-container">
            {getVisibleCards().map((t, i) => (
              <div
                key={`${t.id}-${i}`}
                className={`customerreviewtestimonial-card ${
                  i === 1 ? 'customerreviewcard-center' : i === 0 ? 'customerreviewcard-left' : 'customerreviewcard-right'
                }`}
              >
                <div className="customerreviewcard-inner">
                  <img
                    src={t.customer?.image || DEFAULT_AVATAR}
                    className="customerreviewtestimonial-image"
                    alt={t.customerName ?? 'Customer'}
                  />
                  <h3 className="customerreviewtestimonial-name">{t.customerName ?? 'Customer'}</h3>

                  <div className="customerreviewtestimonial-rating">
                    {[...Array(t.rating)].map((_, idx) => (
                      <Star key={idx} size={16} fill="#FFD700" color="#FFD700" />
                    ))}
                  </div>

                  <p className="customerreviewtestimonial-text">{t.comment}</p>
                </div>
              </div>
            ))}
          </div>

          <button className="customerreviewnav-arrow customerreviewnext-arrow" onClick={handleNext}>
            <ChevronRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default TestimonialsSection;